import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { FormProvider, useForm } from "react-hook-form";
import { loadStripe } from "@stripe/stripe-js/pure";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation, useQuery } from "urql";
import { isLoggedInSelector, login } from "../../redux/slices/loginSlice";
import { RECAPTCHA_VERIFY_QUERY, SUBSCRIPTIONS_PLAN_QUERY } from "../../graphql/queries";
import {
  billingPlatform,
  EMAIL_CHAR_LIMIT,
  language,
  recaptchaSiteKey,
  roleKey,
  stripePublishableKeys,
  userRoles,
  variousURLs,
} from "../../config/constants";
import LoadingAnim from "../global/loadingAnim";
import { gtmLogInOut, gtmSubscribed } from "../../utilities/gtm";
import ErrorMessage from "../global/errorMessage";
import routeCodes from "../../config/routeCodes";
import { getStripeLocale } from "../../utilities/stripe";
import {
  mpFieldValidationFailed,
  mpPaymentSubmitted,
  mpSignUpSuccess,
  mpSubStarted,
  mpSubscriptionFailed,
  getErrorMessageText,
  inferSubmissionFailureReasonAndType,
  inferInlineValidationErrorType,
  mpLoginSuccess,
} from "../../utilities/mixpanel";
import { setItem } from "../../utilities/localStorage";
import { subscribeSubmit, submitError } from "../../utilities/subscribe";
import { metaDefault } from "../../config/meta";
import FamilyPlans from "./familyPlans";
import ClassroomPlans from "./classroomPlans";
import SchoolPlans from "./schoolPlans";
import { SUBSCRIPTION_PURCHASE } from "../../graphql/mutations";
import { useLazyQuery } from "../../hooks";
import TextViewHook from "../global/textViewHook";
import { getMaxLengthValidator, validateEmail } from "../../utilities/forms/rules";
import familyPlanIcon1 from "../../../assets/img/familyPlanIcon1.svg";
import familyPlanIcon2 from "../../../assets/img/familyPlanIcon2.svg";
import classroomPlanIcon1 from "../../../assets/img/classroomPlanIcon1.svg";
import classroomPlanIcon2 from "../../../assets/img/classroomPlanIcon2.svg";
import { scrollDocVerticallyToTarget } from "../../utilities/scroll";
import FlareShimmer from "../global/FlareShimmer";
import Shimmer from "../../../assets/img/shimmer.png";
import Arrow from "../../../assets/svg/arrowRight.svg";
import Testimonials from "./Testimonials";
import Faq from "./Faq";
import { useDispatch, useSelector } from "react-redux";
import { setAuthCookie } from "../../utilities/cookies";
import { setShowAccountComplete } from "../../redux/slices/accountCompleteSlice";
import { isCrawler, isPrerender } from "../../utilities/detection";
import { getLangCode } from "../../config";
import { getPlanData, getRoleFromJwt, getString, handlePostRequest, isAccountIncomplete } from "../../utilities";

const SubscribeContent = ({ userRole }) => {
  const isBot = isCrawler || isPrerender;

  // duration should be long enough for the number of...
  // transition-count-# classes times sass transition speeds and delays
  // _subscribe.scss
  // $subscribe-transition-stagger-duration
  // $subscribe-transition-duration
  const transitionDuration = isBot ? 0 : 1000;

  const cardElementStyles = {
    base: {
      fontFamily: `"montserrat", sans-serif`,
      color: "#38352f",
      "::placeholder": {
        color: "#38352f",
      },
    },
    invalid: {
      color: "#38352f",
      iconColor: "#FE6040",
    },
  };

  const isLoggedIn = useSelector(isLoggedInSelector);

  const defaultPlanSelection = "m12"; // default annual 12 months
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const langCode = getLangCode();

  const formRef = useRef(null);
  const isSwitchingPlanRef = useRef(false); // don't track field validation errors when switching plans
  const transitionTimerRefOff = useRef();
  const transitionTimerRefOn = useRef();
  const familyPlanSelectRef = useRef();
  const classroomPlanSelectRef = useRef();

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transition, setTransitionAnim] = useState("off");
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [selectedSubsData, setSelectedSubsData] = useState(null);
  const [selectedSubsPlan, setSelectedSubsPlan] = useState(defaultPlanSelection);
  const [submitErrors, setSubmitErrors] = useState(null);
  const [cardElementErrors, setCardElementErrors] = useState({});
  const [cardElementFocused, setCardElementFocused] = useState(null);
  const [fontSize, setFontSize] = useState("16px");
  const [submitting, setSubmitting] = useState(false);

  // It solves the issue of card validation error is set by onChange when switching from Focus to Blur, causing onBlur does not get the error.
  const cardValidationTrackingRef = useRef({
    cardNumber: { isFocused: false, hasTrackedInFocusSession: false },
    cardExpiry: { isFocused: false, hasTrackedInFocusSession: false },
  });

  const isFamily = userRole === userRoles.family;
  const isClassroom = userRole === userRoles.classroom;
  const isSchool = userRole === userRoles.school;

  const actionStrings = {
    [userRoles.family]: "Family_Form_Submission",
    [userRoles.classroom]: "Classroom_Form_Submission",
    [userRoles.school]: "School_Form_Submission",
  };

  const metaTitleStringPaths = {
    [userRoles.family]: "subscribe.plans.family.title.2",
    [userRoles.classroom]: "subscribe.plans.classroom.title.2",
    [userRoles.school]: "subscribe.plans.school.title.2",
  };

  const subPlans = {
    [userRoles.family]: FamilyPlans,
    [userRoles.classroom]: ClassroomPlans,
    [userRoles.school]: SchoolPlans,
  };

  const action = actionStrings[userRole];
  const SubPlansComponent = subPlans[userRole];

  const methods = useForm({ mode: "onBlur", reValidateMode: "onChange" });
  const {
    clearErrors,
    getFieldState,
    getValues,
    handleSubmit,
    setFocus,
    trigger,
    formState: { errors, isSubmitted },
  } = methods;

  const [subsResult] = useQuery({
    query: SUBSCRIPTIONS_PLAN_QUERY,
    variables: { billingPlatform },
  });
  const { fetching: subsLoading, error: subsError, data: subsData } = subsResult;

  const startTime = useRef(new Date());
  const isPaymentSubmitRetried = useRef(false);

  const trackSubscriptionFailed = ({ reason = "other", errorType = "other", messageDisplayed = "" }) => {
    if (!selectedSubsData?.[selectedSubsPlan]) {
      return;
    }

    mpSubscriptionFailed({
      months: selectedSubsPlan,
      planGroup: userRole,
      pathname: location.pathname,
      reason,
      errorType,
      messageDisplayed,
    });
  };

  const setSubmitErrorsWithTracking = (error) => {
    try {
      if (!error) {
        return;
      }
      const messageDisplayed = getErrorMessageText(error);
      const { reason, errorType } = inferSubmissionFailureReasonAndType(messageDisplayed);
      trackSubscriptionFailed({ reason, errorType, messageDisplayed });
    } catch (e) {}

    setSubmitErrors(error);
  };

  const trackFieldValidationFailed = ({ fieldName = "", errorMessageDisplayed = "" }) => {
    if (!fieldName || !errorMessageDisplayed) {
      return;
    }

    mpFieldValidationFailed({
      fieldName,
      errorType: inferInlineValidationErrorType(fieldName),
      errorMessageDisplayed,
      form: "subscription_form",
    });
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setFontSize("12px"); // Mobile
      } else if (window.innerWidth < 1440) {
        setFontSize("16px");
      } else {
        setFontSize("18px");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      if (transitionTimerRefOff.current) {
        clearTimeout(transitionTimerRefOff.current);
      }

      if (transitionTimerRefOn.current) {
        clearTimeout(transitionTimerRefOn.current);
      }
    };
  }, []);

  useEffect(() => {
    if (elements) {
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardCvcElement = elements?.getElement(CardCvcElement);
      const cardExpiryElement = elements?.getElement(CardExpiryElement);

      if (cardNumberElement) {
        const updatedStyleObject = {
          style: {
            base: {
              fontSize,
            },
          },
        };

        cardNumberElement.update(updatedStyleObject);
        cardCvcElement.update(updatedStyleObject);
        cardExpiryElement.update(updatedStyleObject);
      }
    }
  }, [fontSize, elements]);

  useEffect(() => {
    isSwitchingPlanRef.current = false;
    setSelectedSubsPlan(defaultPlanSelection);
    setSubmitErrors(null);
    clearErrors();

    const cardCvcElement = elements?.getElement(CardCvcElement);
    const cardExpiryElement = elements?.getElement(CardExpiryElement);

    if (cardCvcElement) {
      cardCvcElement.clear();
    }

    if (cardExpiryElement) {
      cardExpiryElement.clear();
    }

    if (subsData) {
      setSelectedSubsData(getPlanData(subsData, userRole));
      setIsInitialMount(false);

      // apply on styles
      setTransitionAnim("on");
      transitionTimerRefOn.current = setTimeout(() => {
        setIsTransitioning(false);
      }, transitionDuration);
    }
  }, [userRole, subsData]);

  const onSubPurchaseCompleted = (data) => {
    const { planId, amount, name } = selectedSubsData[selectedSubsPlan];
    const {
      subscriptionWebPurchase: {
        subscription,
        invoiceId,
        periodStart,
        periodEnd,
        subStartDate,
        token,
        games,
        hiddenCategories,
        email,
        id,
        createdAt,
        logins,
      },
    } = data;

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardCvcElement = elements.getElement(CardCvcElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);

    if (cardNumberElement) {
      cardNumberElement.clear();
      cardCvcElement.clear();
      cardExpiryElement.clear();
    }

    const tokenRole = getRoleFromJwt(token);

    dispatch(
      login({
        role: tokenRole,
        planId: subscription.planId,
        billingPlatform: subscription.billingPlatform,
        games,
        hiddenCategories,
        email,
        id,
        hasPassword: false,
      }),
    );
    setItem("planId", subscription.planId);
    setItem("billingPlatform", subscription.billingPlatform);
    setItem("games", JSON.stringify(games));
    setItem("hiddenCategories", JSON.stringify(hiddenCategories));
    setItem(roleKey, tokenRole);
    setItem("subMonths", subscription.months);
    setAuthCookie();

    dispatch(
      setShowAccountComplete({
        showAccountComplete: isAccountIncomplete(tokenRole, subscription.billingPlatform, false),
      }),
    );

    setSubmitErrors(null);
    setSubmitting(false);

    gtmSubscribed({ planId, amount, name }, { invoiceId });
    mpSubStarted({
      startType: "new",
      months: subscription?.months,
      planGroup: subscription?.planGroup,
      amount: subscription?.amount,
      periodStart,
      periodEnd,
      subStartDate,
      pathname: location.pathname,
    });
    mpSignUpSuccess("page", subscription?.planGroup, location.pathname);

    gtmLogInOut({ role: tokenRole, billingPlatform: subscription.billingPlatform });
    mpLoginSuccess(tokenRole, logins, false, subscription.planGroup, id, createdAt, location.pathname);
  };

  const [subPurchaseResult, subPurchase] = useMutation(SUBSCRIPTION_PURCHASE);
  const { fetching: subPurchaseLoading } = subPurchaseResult;

  const onRecaptchaCompleted = async (data) => {
    const { email } = getValues();
    const { planId, amount, name } = selectedSubsData[selectedSubsPlan];

    await subscribeSubmit({
      data,
      setSubmitErrors: setSubmitErrorsWithTracking,
      cardElement: elements.getElement(CardNumberElement),
      mutation: (variables) => {
        subPurchase(variables).then((result) => {
          handlePostRequest(result, onSubPurchaseCompleted, (error) => {
            setSubmitting(false);
            setSubmitErrorsWithTracking(error);
          });
        });
      },
      action,
      stripe,
      myVariables: {
        email: email.trim(),
        planId,
      },
      plan: { planId, amount, name },
      setSubmitting,
    });
  };

  const onRecaptchaError = (error) => {
    console.log("recaptcha error: ", error);
    trackSubscriptionFailed({
      reason: "recaptcha_failed",
      errorType: "recaptcha_error",
      messageDisplayed: getErrorMessageText(error),
    });
    setSubmitting(false);
    submitError(error, setSubmitErrors, setSubmitting);
  };

  const { executeQuery: recaptchaVerify, loading: recaptchaLoading } = useLazyQuery(
    RECAPTCHA_VERIFY_QUERY,
    onRecaptchaCompleted,
    onRecaptchaError,
  );

  const resetSubmitOnChange = () => {
    if (submitErrors) {
      setSubmitErrors(null);
    }
  };

  const getErrors = (name) => {
    if (!isSubmitted && errors[name]?.type === "required") {
      return {};
    }

    return errors;
  };

  const fieldNameMap = {
    cardNumber: "card_number",
    cardExpiry: "card_expiration",
  };

  const cardElementOnBlur = ({ elementType }) => {
    const currentTracking = cardValidationTrackingRef.current[elementType];
    const fieldName = fieldNameMap[elementType];

    // Fallback: if an error is already available at blur time and we have not
    // tracked in this focus cycle yet, emit one validation failure event.
    if (fieldName && currentTracking && !currentTracking.hasTrackedInFocusSession && cardElementErrors[elementType]) {
      trackFieldValidationFailed({
        fieldName,
        errorMessageDisplayed: cardElementErrors[elementType],
      });
      currentTracking.hasTrackedInFocusSession = true;
    }

    if (currentTracking) {
      currentTracking.isFocused = false;
    }

    setCardElementFocused((prevType) => {
      if (prevType === elementType) {
        return null;
      }
      return prevType;
    });
  };

  const cardElementOnFocus = ({ elementType }) => {
    if (cardValidationTrackingRef.current[elementType]) {
      cardValidationTrackingRef.current[elementType] = {
        isFocused: true,
        hasTrackedInFocusSession: false,
      };
    }
    setCardElementFocused(elementType);
  };

  const cardElementOnChange = ({ error, elementType }) => {
    const currentTracking = cardValidationTrackingRef.current[elementType];
    const fieldName = fieldNameMap[elementType];

    if (error?.message && fieldName && currentTracking?.isFocused && !currentTracking.hasTrackedInFocusSession) {
      trackFieldValidationFailed({
        fieldName,
        errorMessageDisplayed: error.message,
      });
      currentTracking.hasTrackedInFocusSession = true;
    }

    if (error) {
      setCardElementErrors((prevElements) => ({ ...prevElements, [elementType]: error.message }));
    } else if (!error && elementType && cardElementErrors[elementType]) {
      setCardElementErrors(({ [elementType]: deletedKey, ...errorsWithoutType }) => ({ ...errorsWithoutType }));
    }

    resetSubmitOnChange();
  };

  const handleCardElementErrors = () => {
    if (Object.keys(cardElementErrors).length > 0) {
      const errorMessage =
        cardElementErrors[cardElementFocused] || cardElementErrors.cardNumber || Object.values(cardElementErrors)[0];
      return (
        <div className="subscribe-card-elements-errors" role="alert" aria-live="polite">
          {errorMessage}
        </div>
      );
    }
    return null;
  };

  const handleSummaryText = () => {
    let term = "annual";
    if (isFamily) {
      if (selectedSubsPlan === "m1") {
        term = "month";
      } else if (langCode === language.default) {
        term = "year";
      }
    }
    return getString(`subscribe.summary.${userRole?.toLowerCase()}.${term}`);
  };

  const handleNavigate = (route, shouldNavigate = true) => {
    if (shouldNavigate || isTransitioning) {
      if (isTransitioning) {
        setIsTransitioning(false);
        setTransitionAnim("on");
        if (transitionTimerRefOff.current) {
          clearTimeout(transitionTimerRefOff.current);
        }
        if (transitionTimerRefOn.current) {
          clearTimeout(transitionTimerRefOn.current);
        }
        navigate(route);
      } else {
        setIsTransitioning(true);
        setTransitionAnim("off");
        transitionTimerRefOff.current = setTimeout(() => {
          navigate(route);
        }, transitionDuration);
      }
    }
  };

  // family and classroom same header text
  let titleHeaderStringPath = "join.3";
  const mainContainerStyles = ["subscribe-form-wrapper"];
  const planSelectBaseStyles = ["button-flat-color", "subscribe-plan", "subscribe-focus", langCode];
  const cardElementsContainerStyles = ["subscribe-card-elements-container"];
  let transitionClass = `subscribe-transition ${transition === "on" ? "transition-on" : ""}`;

  if (isBot) {
    transitionClass = "";
  }

  if (isSchool) {
    titleHeaderStringPath = "subscribe.plans.school.title.1";
    mainContainerStyles.push("school");
  }

  const emailRequiredReplaceStringPath = langCode === language.la ? "forms.email.6" : "forms.email.2";
  const emailValidateStringPath = langCode === language.la ? "forms.email.6" : "forms.email.0";
  const emailLabelStringPath = langCode === language.la ? "forms.email.2" : "forms.email.0";

  const isLoading = submitting || subsLoading || subPurchaseLoading || recaptchaLoading;

  const isDisabled = !selectedSubsData || subsError || isLoading || isLoggedIn;
  const markSwitchingPlan = () => {
    isSwitchingPlanRef.current = true;
  };
  const markSwitchingPlanOnKeyDown = (event) => {
    if (event.key === "Enter") {
      markSwitchingPlan();
    }
  };

  if (isDisabled) {
    cardElementsContainerStyles.push("is-disabled");
  }

  return (
    <div className="subscribe-wrapper">
      {metaDefault({
        path: location.pathname,
        title: `${getString("join.3")} • ${getString(metaTitleStringPaths[userRole])}`,
      })}

      <div className={mainContainerStyles.join(" ")}>
        <div className="subscribe-container">
          <h1>{getString(titleHeaderStringPath)}</h1>

          {userRole !== userRoles.school && (
            <>
              <h2>{getString("subscribe.cta")}</h2>
              <div role="radiogroup" className="subscribe-plan-select">
                <button
                  ref={familyPlanSelectRef}
                  type="button"
                  role="radio"
                  aria-checked={isFamily}
                  onClick={() => {
                    handleNavigate(routeCodes.SUBSCRIBE_FAMILY, !isFamily);
                    familyPlanSelectRef.current?.classList.add("active");
                    classroomPlanSelectRef.current?.classList.remove("active");
                  }}
                  className={[...planSelectBaseStyles, ...(isFamily && !isInitialMount ? ["active"] : [])].join(" ")}
                  tabIndex={0}
                  onMouseDown={markSwitchingPlan}
                  onPointerDown={markSwitchingPlan}
                  onKeyDown={markSwitchingPlanOnKeyDown}
                >
                  {getString(
                    langCode === language.la ? "subscribe.plans.family.title.5" : "subscribe.plans.family.title.2",
                  )}
                  <div className="subscribe-plan-icons family">
                    <img src={familyPlanIcon1} draggable="false" width="151" height="71" loading="lazy" alt="" />
                    <img src={familyPlanIcon2} draggable="false" width="81" height="26" loading="lazy" alt="" />
                  </div>
                </button>

                <button
                  ref={classroomPlanSelectRef}
                  type="button"
                  role="radio"
                  aria-checked={isClassroom}
                  onClick={() => {
                    handleNavigate(routeCodes.SUBSCRIBE_CLASSROOM, !isClassroom);
                    familyPlanSelectRef.current?.classList.remove("active");
                    classroomPlanSelectRef.current?.classList.add("active");
                  }}
                  className={[...planSelectBaseStyles, ...(isClassroom && !isInitialMount ? ["active"] : [])].join(" ")}
                  tabIndex={0}
                  onMouseDown={markSwitchingPlan}
                  onPointerDown={markSwitchingPlan}
                  onKeyDown={markSwitchingPlanOnKeyDown}
                  id="classroom-plan"
                >
                  {getString(
                    langCode === language.la
                      ? "subscribe.plans.classroom.title.6"
                      : "subscribe.plans.classroom.title.2",
                  )}
                  <div className="subscribe-plan-icons classroom">
                    <img src={classroomPlanIcon1} draggable="false" width="28" height="37" loading="lazy" alt="" />
                    <img src={classroomPlanIcon2} draggable="false" width="63" height="33" loading="lazy" alt="" />
                  </div>
                </button>
              </div>
            </>
          )}

          {subsError && <ErrorMessage error={subsError} />}

          <SubPlansComponent
            className={transitionClass}
            plan={selectedSubsPlan}
            setPlan={setSelectedSubsPlan}
            planData={selectedSubsData}
            activeClick={() => {
              scrollDocVerticallyToTarget(formRef.current);
              setFocus("email");
            }}
            {...(!isFamily && { handleNavigate })}
          />

          <h2 className={`${transitionClass} transition-count-3`}>{getString("subscribe.create")}</h2>

          <FormProvider {...methods}>
            <form
              className={`subscribe-form ${transitionClass} transition-count-4`}
              ref={formRef}
              autoComplete="off"
              onSubmit={handleSubmit(
                () => {
                  if (!isDisabled) {
                    setSubmitting(true);

                    // Mixpanel Event
                    const elapsed = Math.floor((new Date() - startTime.current) / 1000);
                    mpPaymentSubmitted({
                      months: selectedSubsPlan,
                      planGroup: userRole,
                      amount: selectedSubsData[selectedSubsPlan].amount,
                      pathname: location.pathname,
                      elapsed,
                      isRetry: isPaymentSubmitRetried.current,
                    });
                    isPaymentSubmitRetried.current = true;

                    // Recaptcha
                    window.grecaptcha.enterprise.ready(async () => {
                      const token = await window.grecaptcha.enterprise.execute(recaptchaSiteKey, { action });

                      if (token) {
                        recaptchaVerify({ token, action, verify: true, siteKey: recaptchaSiteKey });
                      }
                    });
                  }
                },
                (formErrors) => {
                  const emailError = formErrors?.email;

                  if (emailError) {
                    const reason = emailError.type === "required" ? "empty_field" : "invalid_field";
                    const errorType = emailError.type === "required" ? "empty_email" : "invalid_email_format";

                    trackSubscriptionFailed({
                      reason,
                      errorType,
                      messageDisplayed: emailError.message || "",
                    });
                  } else {
                    trackSubscriptionFailed({
                      reason: "other",
                      errorType: "other",
                      messageDisplayed: "",
                    });
                  }
                },
              )}
            >
              <h3>{handleSummaryText()}</h3>

              <TextViewHook
                rules={{
                  required: getString("forms.errorMessages.isRequired.0", {
                    replace: [getString(emailRequiredReplaceStringPath)],
                  }),
                  validate: (value) => validateEmail(value, getString(emailValidateStringPath)),
                  maxLength: getMaxLengthValidator(getString(emailLabelStringPath), EMAIL_CHAR_LIMIT),
                  onBlur: async () => {
                    if (isSwitchingPlanRef.current) {
                      return;
                    }
                    const isValid = await trigger("email");
                    if (!isValid) {
                      const emailFieldError = getFieldState("email").error;
                      if (emailFieldError?.type !== "required" || isSubmitted) {
                        trackFieldValidationFailed({
                          fieldName: "email",
                          errorMessageDisplayed: emailFieldError?.message || "",
                        });
                      }
                    }
                  },
                }}
                errors={getErrors("email")}
                className="col1"
                name="email"
                placeholder={getString(emailLabelStringPath)}
                label={getString(emailLabelStringPath)}
                shouldLabelAnimate={false}
                onChange={() => {
                  if (errors.email) {
                    trigger("email");
                  }
                  resetSubmitOnChange();
                }}
                isDisabled={isDisabled}
                autoComplete="off"
              />

              <div className={cardElementsContainerStyles.join(" ")}>
                <div className={cardElementErrors.cardNumber ? "form-field-error" : ""}>
                  <label htmlFor="card-number-element" className={langCode}>
                    {getString("forms.cardNumber")}
                  </label>
                  <CardNumberElement
                    id="card-number-element"
                    onChange={cardElementOnChange}
                    onFocus={cardElementOnFocus}
                    onBlur={cardElementOnBlur}
                    options={{
                      placeholder: getString("forms.cardNumber"),
                      disabled: isDisabled,
                      disableLink: true,
                      style: {
                        base: {
                          ...cardElementStyles.base,
                          ...(langCode === language.default && { textTransform: "capitalize" }),
                        },
                        invalid: cardElementStyles.invalid,
                      },
                    }}
                  />
                </div>

                <div className={cardElementErrors.cardExpiry ? "form-field-error" : ""}>
                  <label htmlFor="card-expiry-element" className={langCode}>
                    {getString("forms.expires")}
                  </label>
                  <CardExpiryElement
                    id="card-expiry-element"
                    onChange={cardElementOnChange}
                    onFocus={cardElementOnFocus}
                    onBlur={cardElementOnBlur}
                    options={{
                      disabled: isDisabled,
                      style: {
                        base: {
                          textAlign: "center",
                          ...cardElementStyles.base,
                        },
                        invalid: cardElementStyles.invalid,
                      },
                    }}
                  />
                </div>

                <div className={cardElementErrors.cardCvc ? "form-field-error" : ""}>
                  <label htmlFor="card-cvc-element" className={langCode}>
                    {getString("forms.cvc")}
                  </label>
                  <CardCvcElement
                    id="card-cvc-element"
                    onChange={cardElementOnChange}
                    onFocus={cardElementOnFocus}
                    onBlur={cardElementOnBlur}
                    options={{
                      disabled: isDisabled,
                      style: {
                        base: {
                          textAlign: "center",
                          ...cardElementStyles.base,
                        },
                        invalid: cardElementStyles.invalid,
                      },
                    }}
                  />
                </div>
              </div>
              {handleCardElementErrors()}

              {!!submitErrors && <ErrorMessage error={submitErrors} />}

              <button type="submit" className="subscribe-premium-submit premium-join" disabled={isDisabled}>
                <FlareShimmer filePath={Shimmer} />
                {getString("subscribe.submit")}
              </button>

              {userRole !== userRoles.family && langCode === language.default && (
                <div className="subscribe-purchase-order">
                  <div>{getString("subscribe.plans.school.po", { html: true })}</div>
                  <Link
                    data-testid="schoolSubCtaRFQ"
                    type="button"
                    to={routeCodes.REQUEST_QUOTE}
                    className="planbutton button-flat-color pt-cyan round auto sub-cta-link"
                  >
                    {getString("subscribe.plans.school.requestQuote").toUpperCase()}
                    <Arrow className="inline-arrow" />
                  </Link>
                </div>
              )}
            </form>
          </FormProvider>

          <div className={`subscribe-terms ${transitionClass} transition-count-5`}>
            {getString("subscribe.terms", {
              replace: [langCode === language.la ? getString("subscribe.title.1") : getString("subscribe.title.0")],
              html: true,
            })}
          </div>
        </div>
      </div>

      <Testimonials userRole={userRole} className={`${transitionClass} transition-count-6`} />

      <Faq userRole={userRole} className={`${transitionClass} transition-count-7`} />

      {isLoading && (
        <LoadingAnim position="fixed" className="background-transparent-pt-dark-space orange">
          {!subsLoading && <h4>{getString("subscribe.loading")}</h4>}
        </LoadingAnim>
      )}
    </div>
  );
};

export default function Subscribe(props) {
  const stripePromise = useMemo(() => loadStripe(stripePublishableKeys[process.env.NODE_ENV]), []);

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: getStripeLocale(),
        fonts: [
          {
            cssSrc: variousURLs.googleFonts.montserrat.regular,
          },
        ],
      }}
    >
      <SubscribeContent {...props} />
    </Elements>
  );
}
